/* Seitenleiste „Textur“: Textur-Modus, WADs, Sprites, Skybox, Sounds, BSP-Texturen, aktive Textur, benutzte Texturen und alle Texturen. */
import { memo, useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import Skybox from '../../formats/skybox.ts';
import Spr from '../../formats/spr.ts';
import { bspTexSummary, expectedWads, filterTextures, soundNeeds, spriteNeeds, usedInfo, wadLoaded } from '../../model/texlist.ts';
import type { UsedTex } from '../../model/texlist.ts';
import { useT } from '../hooks.ts';
import { TexMode } from './TexMode.tsx';
import { createNotifier, useNotifier } from '../notifier.ts';
import type { TexApp, TexShared } from './types.ts';
import { CommitInput, Section, TexCanvas } from './widgets.tsx';

const click = (id: string) => () => document.getElementById(id)?.click();

function NeedList(props: { items: Map<string, number>; has: (k: string) => boolean }) {
  const t = useT();
  if (!props.items.size) return null;
  return (
    <div className="wadlist">
      {Array.from(props.items.entries()).map(([k, n]) => {
        const ok = props.has(k);
        return <div key={k} className={'wadrow ' + (ok ? 'wok' : 'wbad')}>{(ok ? '✓ ' : '✗ ') + k + ' (' + n + '×)' + (ok ? '' : '  – ' + t('pn.not_loaded'))}</div>;
      })}
    </div>
  );
}

function WadSection({ app }: { app: TexApp }) {
  const t = useT();
  const expected = expectedWads(app.doc.ents[0].kv.wad);
  return (
    <>
      <h3>{t('pn.wad_files')}</h3>
      <div className="row">
        <button className="btn sm primary" onClick={click('f-wad')}>{t('pn.load_wad')}</button>
        <button className="btn sm" onClick={click('f-spr')}>{t('pn.load_sprites')}</button>
        {app.wadNames.length > 0 && <button className="btn sm" onClick={() => app.clearWads()}>{t('pn.remove_all')}</button>}
        {app.assets && <button className="btn sm" onClick={() => app.assets?.open()}>{t('ab.open')}</button>}
      </div>
      <p className="small">{app.wadNames.length ? t('pn.loaded_wads', { names: app.wadNames.join(', '), count: app.wads.count() }) : t('pn.load_wad_hint')}</p>
      {expected.length > 0 && (
        <div className="wadlist">
          <div className="small">{t('pn.expects_wads')}</div>
          {expected.map((n) => { const ok = wadLoaded(app.wadNames, n); return <div key={n} className={'wadrow ' + (ok ? 'wok' : 'wbad')}>{(ok ? '✓ ' : '✗ ') + n + (ok ? '' : '  – ' + t('pn.not_loaded'))}</div>; })}
        </div>
      )}
    </>
  );
}

function SpriteSection({ app }: { app: TexApp }) {
  const t = useT();
  const need = spriteNeeds(app.doc, Spr.key);
  if (!need.size && !app.sprites.size && !app.assets) return null;
  return (
    <Section title={t('pn.sprites')}>
      {app.assets && <div className="row"><button className="btn sm" onClick={() => app.assets?.open('sprite')}>{t('ab.browse')}</button></div>}
      <p className="small">{t('pn.sprites_note', { count: app.sprites.size })}</p>
      <NeedList items={need} has={(k) => app.sprites.has(k)} />
    </Section>
  );
}

function SkySection({ app }: { app: TexApp }) {
  const t = useT();
  const sb = app.skybox, wanted = String(app.doc.ents[0].kv.skyname || ''), have = Skybox.SIDES.filter((x) => sb.imgs[x]);
  // Der Schalter hält seinen Zustand selbst (wie bei den Sounds): app.skybox.on ändert sich ohne Neuzeichnen der Seite, ein gesteuertes
  // Kontrollkästchen sprang sonst zurück. Eine neu geladene Skybox (sb.on = true) wird übernommen.
  const [on, setOn] = useState(sb.on);
  useEffect(() => { setOn(sb.on); }, [sb, sb.on]);
  return (
    <Section title={t('pn.skybox')}>
      {app.assets && <div className="row"><button className="btn sm" onClick={() => app.assets?.open('skybox')}>{t('ab.browse')}</button></div>}
      <div className="row">
        <button className="btn sm primary" title={t('pn.load_sky_tip')} onClick={click('f-sky')}>{t('pn.load_sky')}</button>
        <label className="chk"><input type="checkbox" checked={on} onChange={(e) => { setOn(e.target.checked); app.setSkyOn(e.target.checked); }} />{t('pn.show_in_3d')}</label>
      </div>
      {sb.preview && sb.name && sb.name.toLowerCase() !== wanted.trim().toLowerCase() && (
        <div className="row"><span className="small">{t('pn.sky_preview_note', { name: sb.name })}</span><button className="btn sm" onClick={() => app.setSkyPreview(false)}>{t('pn.sky_preview_end')}</button></div>
      )}
      {sb.mats ? (
        <div className="row">
          {['up', 'dn'].map((sd) => (
            <label key={sd} className="chk">{t(sd === 'up' ? 'pn.sky_rot_up' : 'pn.sky_rot_dn') + ' '}
              <select value={sb.rot[sd]} onChange={(e) => app.setSkyRot(sd, +e.target.value)}>{[0, 1, 2, 3].map((k) => <option key={k} value={k}>{k * 90 + '°'}</option>)}</select>
            </label>
          ))}
        </div>
      ) : <span />}
      <p className="small">{(sb.name ? t('pn.sky_loaded', { name: sb.name, have: have.length }) + (sb.mats ? ' ✓' : '') : t('pn.sky_none')) + (wanted ? ' · ' + t('pn.sky_wants', { name: wanted }) : '') + '. ' + t('pn.sky_note')}</p>
    </Section>
  );
}

function SoundSection({ app }: { app: TexApp }) {
  const t = useT();
  const need = soundNeeds(app.doc, app.soundKey);
  // Der Schalter hält seinen Zustand selbst: app.spatial ändert sich ohne Neuzeichnen der Seite, ein gesteuertes Kontrollkästchen
  // sprang sonst sofort zurück und ließ sich nicht einschalten
  const [spatial, setSpatial] = useState(app.spatial);
  if (!need.size && !app.sounds.size && !app.assets) return null;
  return (
    <Section title={t('pn.sounds')}>
      {app.assets && <div className="row"><button className="btn sm" onClick={() => app.assets?.open('sound')}>{t('ab.browse')}</button></div>}
      <div className="row">
        <button className="btn sm primary" title={t('pn.load_sounds_tip')} onClick={click('f-snd')}>{t('pn.load_sounds')}</button>
        <label className="chk"><input type="checkbox" checked={spatial} onChange={(e) => { setSpatial(e.target.checked); app.setSpatial(e.target.checked); }} />{t('pn.spatial')}</label>
      </div>
      <p className="small">{t('pn.sounds_note', { count: app.sounds.size })}</p>
      <p className="small"><a href="https://hl1sfx.com" target="_blank" rel="noopener noreferrer">{t('pn.sounds_ref')}</a></p>
      <NeedList items={need} has={(k) => app.hasSound(k)} />
    </Section>
  );
}

function BspSection({ app }: { app: TexApp }) {
  const t = useT();
  if (!app.bsp) return null;
  const s = bspTexSummary(app.bsp.data.textures, (n) => app.wads.has(n));
  return (
    <Section title={t('pn.bsp_textures')}>
      <p className="small">{t('pn.bsp_tex_summary', { total: s.total, emb: s.emb, wad: s.wad, miss: s.miss })}</p>
      {s.miss > 0 && <div className="warnbox">{t('pn.bsp_tex_missing', { names: s.missNames.slice(0, 60).join(', ') + (s.miss > 60 ? ' …' : '') })}</div>}
    </Section>
  );
}

function ActiveSection({ app }: { app: TexApp }) {
  const t = useT();
  const [ok, setOk] = useState(false);
  const sz = app.wads.size(app.activeTex);
  return (
    <Section title={t('pn.active_tex')}>
      <div className="row" style={{ flexWrap: 'nowrap', alignItems: 'flex-start' }}>
        <TexCanvas name={app.activeTex} wads={app.wads} version={app.wadNames.join('|')} className="preview-tex" onDrawn={setOk} />
        <div style={{ display: 'grid', gap: 6, minWidth: 0, flex: 1 }}>
          <CommitInput value={app.activeTex} list="dl-tex" aria-label={t('pn.active_tex')} onCommit={(v) => { const n = v.trim(); if (n) app.setActiveTex(n, false); }} />
          <div className="row">
            <button className="btn sm primary" onClick={() => { if (!app.applyTex(app.activeTex)) app.status(t('pn.nothing_selected_tex')); }}>{t('pn.apply_sel')}</button>
            {app.assets && <button className="btn sm" onClick={() => app.assets?.open('texture')}>{t('ab.browse')}</button>}
          </div>
          <span className="small">{ok && sz ? app.activeTex + ' · ' + sz.w + '×' + sz.h : (app.wads.count() ? t('pn.not_in_wads') : t('pn.no_wad'))}</span>
        </div>
      </div>
    </Section>
  );
}

/** Zeile „benutzte Textur einer BSP“: tauschen gegen eine andere Textur */
function SwapRow({ app, u }: { app: TexApp; u: UsedTex }) {
  const t = useT();
  const [to, setTo] = useState('');
  const orig = app.bsp?.data.textures.find((x) => x.orig && x.name.toUpperCase() === u.name.toUpperCase())?.orig?.name || null;
  const go = () => { if (to.trim()) app.bspReplaceTex(u.name, to); };
  return (
    <div className="texrow">
      <button className={'chip' + (u.ok ? '' : ' miss')} data-name={u.name} title={t('pn.n_faces', { count: u.n })} onClick={() => app.setActiveTex(u.name, true)}>{u.name}</button>
      <span className="small">{u.n + (orig ? ' · ' + t('pn.was', { name: orig }) : '')}</span>
      <input type="text" className="inp" list="dl-tex" autoComplete="off" placeholder={t('find.ph_newtex')} style={{ width: 120 }} value={to} onChange={(e) => setTo(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') go(); }} />
      <button className="btn" onClick={go}>{t('pn.swap')}</button>
      <button className="btn" title={t('pn.swap_active_tip', { tex: app.activeTex })} onClick={() => app.bspReplaceTex(u.name, app.activeTex)}>{t('pn.active')}</button>
      {orig && <button className="btn" title={t('pn.restore_original')} onClick={() => app.bspReplaceTex(u.name, orig)}>↺</button>}
    </div>
  );
}

function UsedSection({ app, used }: { app: TexApp; used: UsedTex[] }) {
  const t = useT();
  if (!used.length) return null;
  const nmiss = used.filter((u) => !u.ok).length;
  const active = app.activeTex.toUpperCase();
  return (
    <>
      <h3>{t('pn.used_in_map')}</h3>
      {nmiss > 0 && <p className="small bad">{t('pn.n_not_loaded', { miss: nmiss, total: used.length })}</p>}
      {app.bsp ? (
        <>
          <p className="small">{t('pn.swap_note')}</p>
          <div className="texrows">{used.map((u) => <SwapRow key={u.name} app={app} u={u} />)}</div>
        </>
      ) : (
        <div className="chips">
          {used.map((u) => (
            <button key={u.name} className={'chip' + (u.ok ? '' : ' miss') + (u.name.toUpperCase() === active ? ' act' : '')} data-name={u.name}
              title={t('pn.n_faces', { count: u.n }) + (u.ok ? '' : ' – ' + t('pn.tex_not_loaded'))} onClick={() => app.setActiveTex(u.name, true)}>{u.name}</button>
          ))}
        </div>
      )}
    </>
  );
}

const Thumb = memo(function Thumb(props: { name: string; act: boolean; wads: TexApp['wads']; version: string; onPick: (n: string) => void }) {
  return (
    <button className={'thumb' + (props.act ? ' act' : '')} data-name={props.name} title={props.name} onClick={() => props.onPick(props.name)}>
      <TexCanvas name={props.name} wads={props.wads} version={props.version} later />
      <span>{props.name}</span>
    </button>
  );
});

function AllSection({ app, shared, used }: { app: TexApp; shared: TexShared; used: UsedTex[] }) {
  const t = useT();
  const [text, setText] = useState(shared.texFilter);
  const [filter, setFilter] = useState(shared.texFilter);
  useEffect(() => { const id = setTimeout(() => { shared.texFilter = text; setFilter(text); }, 200); return () => clearTimeout(id); }, [text, shared]);
  const version = app.wadNames.join('|');
  const usedKey = used.map((u) => u.name).join('|');
  const list = useMemo(() => filterTextures(app.wads.names(), new Set(used.map((u) => u.name.toUpperCase())), filter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [version, usedKey, filter, app.wads.count()]);
  const shown = list.slice(0, 300);
  const active = app.activeTex.toUpperCase();
  const pick = useMemo(() => (n: string) => app.setActiveTex(n, true), [app]);
  if (!app.wads.count()) return null;
  return (
    <>
      <h3>{t('pn.all_textures')}</h3>
      <input type="search" className="inp" style={{ width: '100%' }} placeholder={t('pn.filter_tex')} value={text} onChange={(e) => setText(e.target.value)} />
      <p className="small">{t('pn.hits', { count: list.length }) + (list.length > shown.length ? ', ' + t('pn.first_shown', { count: shown.length }) : '')}</p>
      <div className="thumbs">{shown.map((n) => <Thumb key={n} name={n} act={n.toUpperCase() === active} wads={app.wads} version={version} onPick={pick} />)}</div>
    </>
  );
}

export function TexturePanel(props: { app: TexApp; shared: TexShared; notifier: ReturnType<typeof createNotifier> }) {
  useNotifier(props.notifier);
  const { app, shared } = props;
  const used = usedInfo(app.doc, app.bsp ? { polys: app.bsp.scene.polys, textures: app.bsp.data.textures } : null, (n) => app.wads.has(n));
  return (
    <>
      {app.tool === 'texture' && <TexMode app={app} shared={shared} refresh={props.notifier.notify} />}
      <WadSection app={app} />
      <SpriteSection app={app} />
      <SkySection app={app} />
      <SoundSection app={app} />
      <BspSection app={app} />
      <ActiveSection app={app} />
      <UsedSection app={app} used={used} />
      <AllSection app={app} shared={shared} used={used} />
    </>
  );
}

/** Hängt die Texturseite in das Element ein; refresh() zeichnet nach einer Änderung im Editor neu */
export function mountTexturePanel(el: HTMLElement, app: TexApp, shared: TexShared): { refresh(): void; unmount(): void } {
  const notifier = createNotifier();
  const root = createRoot(el);
  el.textContent = '';
  root.render(<TexturePanel app={app} shared={shared} notifier={notifier} />);
  return { refresh: notifier.notify, unmount: () => root.unmount() };
}
